import { Card, Stack, Typography, Divider, Box } from '@mui/material';
import { ReactElement } from 'react';

import { SlideProductImage } from '@app/components/Company/SlideProductImage';
import { NavigateDetailProductButton } from '@app/components/Product/NavigateDetailProductButton';
import { PropertyDetails } from '@app/components/Product/PropertyDetails';
import { ShowroomList } from '@app/components/Product/ShowroomList';
import { StrengthsSection } from '@app/components/Product/StrengthsSection';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { theme } from '@app/theme';
import { Product } from '@app/types/catalog';

interface ResultItemCardProps {
  onClick?: () => void;
  product: Product;
  showPropertyDetails: boolean;
}

export function ResultItemCard({
  onClick,
  product,
  showPropertyDetails,
}: ResultItemCardProps): ReactElement {
  const { isMobile, isDesktop } = useDeviceType();

  return (
    <Card
      sx={{
        border: `1px solid #ddd`,
        borderRadius: 4,
        boxShadow: '5px 5px 16px rgba(0, 0, 0, 0.1)',
        cursor: onClick ? 'pointer' : 'default',
        maxWidth: '100%',
        px: { sm: 4, xs: 1 },
        py: 3,
      }}
      data-e2e="searchResult-listItem"
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography
            color={theme.palette.primary.dark}
            fontSize={32}
            fontWeight="bold"
          >
            {product.name}
          </Typography>
          {isDesktop && <NavigateDetailProductButton onClick={onClick} />}
        </Stack>
        <Divider
          sx={{ borderColor: theme.palette.blueAccent.main, mb: 2, mt: 1 }}
        />
        <SlideProductImage images={product.images.map((img) => img.url)} />

        <Stack
          direction={isDesktop ? 'row' : 'column'}
          spacing={0}
          sx={{ pt: 4, width: '100%' }}
          divider={
            isDesktop ? (
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: isDesktop ? 3 : 0 }}
              />
            ) : undefined
          }
        >
          {showPropertyDetails && (
            <Box sx={{ width: isDesktop ? '50%' : '100%' }}>
              <PropertyDetails product={product} />
            </Box>
          )}
          <Box
            sx={{
              mt: isDesktop ? 0 : 3,
              width: isDesktop ? '50%' : '100%',
            }}
          >
            <StrengthsSection
              features={product.features}
              strengthsTitle={product.additionalInformation}
              strengthsDescription={product.description}
              showReadMore={true}
            />
          </Box>
        </Stack>

        {!showPropertyDetails && (
          <ShowroomList organizationId={product.organizationId} />
        )}

        {isMobile && <NavigateDetailProductButton onClick={onClick} />}
      </Stack>
    </Card>
  );
}
